import type {
  TableFeatures,
  RowData,
  Column_Internal,
} from '@tanstack/react-table';
import { JSX } from 'react';

import * as styles from './DataTableColumnsListItem.sty.js';
import { Checkbox } from '../../../../../forms/checkbox/Checkbox.js';
import { getColumnLabel } from '../../../../utils/get-column-label.js';

interface ColumnVisibilityCheckboxProps<
  TFeatures extends TableFeatures,
  TData extends RowData,
> {
  /**
   * The column to render the checkbox for.
   */
  column: Column_Internal<TFeatures, TData>;
  /**
   * Whether visibility can be changed for this column.
   */
  enableCheckbox: boolean;
  /**
   * The current visibility state of the column.
   */
  currentState: boolean | 'indeterminate';
  /**
   * Callback when the checkbox is clicked and visibility of the column changes.
   */
  onVisibilityChange: (
    column: Column_Internal<TFeatures, TData>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
}

/**
 * Checkbox component for toggling column visibility in the DataTable column settings.
 * @internal
 */
export function ColumnVisibilityCheckbox<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  props: Readonly<ColumnVisibilityCheckboxProps<TFeatures, TData>>,
): JSX.Element {
  const { enableCheckbox, currentState, onVisibilityChange } = props;

  const columnName = getColumnLabel(props.column);
  const dataDtProperties = `state:${currentState === true ? 'hide' : 'show'}`;

  return (
    <Checkbox
      value={currentState}
      aria-disabled={!enableCheckbox}
      onChange={() => {
        if (enableCheckbox) {
          onVisibilityChange(props.column, currentState);
        }
      }}
      className={styles.columnName}
      data-dt-name="ColumnSettingsShowHideColumn"
      data-dt-properties={dataDtProperties}
    >
      {columnName}
    </Checkbox>
  );
}
